/** Kontekst sesji użytkownika: logowanie, wylogowanie, odtworzenie sesji. */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { authApi } from '@/api/endpoints'
import { tokenStorage } from '@/api/tokenStorage'
import type { User } from '@/api/types'

interface AuthContextValue {
  user: User | null
  /** Trwa odtwarzanie sesji po odświeżeniu strony. */
  isLoading: boolean
  isAuthenticated: boolean
  /** Konto wymaga zmiany hasła — dostęp do reszty aplikacji jest wstrzymany. */
  mustChangePassword: boolean
  login: (email: string, password: string) => Promise<User>
  logout: () => Promise<void>
  /** Kasuje sesję lokalnie, bez odpytywania serwera.
   *  Używane, gdy backend już ją unieważnił — np. po zmianie hasła. */
  clearSession: () => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Wygaśnięcie sesji w dowolnym miejscu aplikacji czyści stan globalnie.
  useEffect(() => {
    tokenStorage.onSessionExpired(() => setUser(null))
    return () => tokenStorage.onSessionExpired(null)
  }, [])

  // Po odświeżeniu strony token dostępowy przepadł (żyje tylko w pamięci),
  // ale token odświeżający pozwala odtworzyć sesję bez ponownego logowania.
  useEffect(() => {
    let cancelled = false

    async function restoreSession() {
      if (!tokenStorage.getRefreshToken()) {
        setIsLoading(false)
        return
      }
      try {
        const profile = await authApi.me()
        if (!cancelled) setUser(profile)
      } catch {
        tokenStorage.clear()
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void restoreSession()
    return () => {
      cancelled = true
    }
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const data = await authApi.login(email, password)
    setUser(data.user)
    return data.user
  }, [])

  const logout = useCallback(async () => {
    await authApi.logout()
    setUser(null)
  }, [])

  const clearSession = useCallback(() => {
    tokenStorage.clear()
    setUser(null)
  }, [])

  const refreshUser = useCallback(async () => {
    setUser(await authApi.me())
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      mustChangePassword: user?.must_change_password ?? false,
      login,
      logout,
      clearSession,
      refreshUser,
    }),
    [user, isLoading, login, logout, clearSession, refreshUser],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth musi być użyte wewnątrz <AuthProvider>.')
  }
  return context
}
